import React, {useState} from "react";
import {
    ExclamationCircleOutlined,
    ExceptionOutlined,
    ClockCircleOutlined,
    CheckSquareOutlined,
    CloseSquareOutlined
} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import {useHistory} from "react-router-dom";
import moment from "moment";
import {ActionButtons, TableContainer} from "../../Components/ComponentStyles";
import {Tooltip} from "antd";
import {ObjectionDetailModal} from "../../objectionDetail/ObjectionDetailModal";
import {CustomButton} from "../../../_defaultComponents/CustomButton/CustomButton";
import {useStore} from "../../../../hooks/useStore";

// PropTypes for ObjectionsTable component
// data: Object - The data to display in the table
// loading: Boolean - Whether the table is in loading state
// setReloadData: Function - Function to trigger data reload
// currentPage: Number - Current page number
// setCurrentPage: Function - Function to set current page
// dataCount: Number - Total number of data items
// reloadData: Object - Reload data object

const ObjectionsTable = ({
    data,
    loading,
    setReloadData,
    currentPage,
    setCurrentPage,
    dataCount,
    reloadData
}) => {
    const {t} = useTranslation(["shiftScore", "objectionStatuses"]);
    const stores = useStore();
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [objectionDetailId, setObjectionDetailId] = useState(null);

    const isMobile = window.innerWidth <= 768;

    const renderActionButtons = (id, item) => {
        const permisions = {
            showObjection: true
        };

        return (
            <ActionButtons>
                {permisions.showObjection && (
                    <Tooltip
                        title={t("shiftScore.actions.showObjectionDetail", {ns: "shiftScore"})}
                    >
                        <CustomButton
                            data-testid="objectionButton"
                            onClick={() => {
                                setDetailModalVisible(true);
                                setObjectionDetailId(item._id);
                            }}
                            icon={<ExceptionOutlined />}
                        ></CustomButton>
                    </Tooltip>
                )}
            </ActionButtons>
        );
    };

    const renderStatus = status => {
        switch (status) {
            case "waitingApprove":
                return t("waitingApprove", {ns: "objectionStatuses"});
            case "approved":
                return t("approved", {ns: "objectionStatuses"});
            case "declined":
                return t("declined", {ns: "objectionStatuses"});
        }
    };

    const getStatusIcon = status => {
        switch (status) {
            case "approved":
                return (
                    <Tooltip title={t("approved", {ns: "objectionStatuses"})}>
                        <CheckSquareOutlined style={{color: "green"}} />
                    </Tooltip>
                );
            case "waitingApprove":
                return (
                    <Tooltip title={t("waitingApprove", {ns: "objectionStatuses"})}>
                        <ClockCircleOutlined style={{color: "orange"}} />
                    </Tooltip>
                );
            case "declined":
                return (
                    <Tooltip title={t("declined", {ns: "objectionStatuses"})}>
                        <CloseSquareOutlined style={{color: "red"}} />
                    </Tooltip>
                );
            default:
                return null;
        }
    };

    return (
        <>
            <ObjectionDetailModal
                isVisible={detailModalVisible}
                setVisible={setDetailModalVisible}
                objectionId={objectionDetailId}
                setReloadData={setReloadData}
                reloadData={reloadData}
            ></ObjectionDetailModal>
            <TableContainer
                size="middle"
                pagination={{
                    total: dataCount,
                    current: currentPage,
                    onChange: async page => {
                        setCurrentPage(page);
                    },
                    hideOnSinglePage: true,
                    showSizeChanger: false
                }}
                rowKey="_id"
                dataSource={data}
                data-testid="shiftTable"
                loading={loading}
                display={data.length > 0 ? "grid" : "flex"}
                dataTitles={
                    data.length > 0
                        ? [
                              t("fields.status", {ns: "common"}),
                              t("fields.date", {ns: "common"}),
                              t("fields.courier", {ns: "common"}),
                              t("fields.company", {ns: "common"}),
                              t("fields.subcompany", {ns: "common"}),
                              t("shiftScore.scheduledShift", {ns: "shiftScore"}),
                              t("shiftScore.startTime", {ns: "shiftScore"}),
                              t("shiftScore.endTime", {ns: "shiftScore"}),
                              t("shiftScore.packageCount", {ns: "shiftScore"}),
                              t("shiftScore.workTime", {ns: "shiftScore"}),
                              t("actions.action", {ns: "common"})
                          ]
                        : []
                }
            >
                <TableContainer.Column
                    dataIndex="status"
                    render={(status, item) => (
                        <>
                            {isMobile ? (
                                <span data-testid="reportDate">{renderStatus(status)}</span>
                            ) : (
                                getStatusIcon(status)
                            )}
                        </>
                    )}
                />
                <TableContainer.Column
                    dataIndex="date"
                    title={t("fields.date", {ns: "common"})}
                    render={(date, item) => (
                        <span data-testid="reportDate">
                            {!item?.shiftReport?.date
                                ? "-"
                                : moment(item.shiftReport.date, "DD-MM-YYYY").format("DD.MM.YYYY")}
                        </span>
                    )}
                />
                <TableContainer.Column
                    dataIndex="fullname"
                    title={t("fields.courier", {ns: "common"})}
                    render={(fullname, item) => (
                        <span data-testid="fullname">{item?.shiftReport?.fullname}</span>
                    )}
                />
                <TableContainer.Column
                    dataIndex="companyName"
                    title={t("fields.company", {ns: "common"})}
                    render={(companyName, item) => (
                        <>
                            <span data-testid="companyName">{item?.shiftReport?.companyName}</span>
                        </>
                    )}
                />
                <TableContainer.Column
                    dataIndex="subCompanyName"
                    title={t("fields.subcompany", {ns: "common"})}
                    render={(subCompanyName, item) => (
                        <>
                            <span data-testid="subCompanyNmae">
                                {item?.shiftReport?.subCompanyName}
                            </span>
                        </>
                    )}
                />

                <TableContainer.Column
                    dataIndex="shiftStartTime"
                    title={t("shiftScore.scheduledShift", {ns: "shiftScore"})}
                    render={(shiftStartTime, item) => (
                        <span data-testid="date">
                            {[
                                item?.shiftReport?.shiftStartTime,
                                item?.shiftReport?.shiftEndTime
                            ].join(" - ")}
                        </span>
                    )}
                />
                <TableContainer.Column
                    dataIndex="startTime"
                    title={t("shiftScore.startTime", {ns: "shiftScore"})}
                    render={(startTime, item) => (
                        <>
                            <span data-testid="startTime-hours">
                                {item?.shiftReport?.startTime
                                    ? moment(item.shiftReport.startTime, "DD.MM.YYYY HH:mm").format(
                                          "HH:mm"
                                      )
                                    : "-"}
                            </span>

                            {stores.meStore.checkRole("notVisibleForCompanyUser") &&
                            item?.shiftReport?.lateStart > 0 ? (
                                <Tooltip
                                    title={
                                        item.shiftReport?.lateStart
                                            ? `${item.shiftReport?.lateStart}${t(
                                                  "units.minute.short",
                                                  {
                                                      ns: "common"
                                                  }
                                              )} ${t("shiftScore.lateShiftStart", {
                                                  ns: "shiftScore"
                                              }).toLocaleLowerCase()}`
                                            : 0
                                    }
                                >
                                    <ExclamationCircleOutlined />
                                </Tooltip>
                            ) : (
                                ""
                            )}
                        </>
                    )}
                />

                <TableContainer.Column
                    dataIndex="endTime"
                    title={t("shiftScore.endTime", {ns: "shiftScore"})}
                    render={(endTime, item) => (
                        <>
                            <span data-testid="endTime-hours">
                                {item?.shiftReport?.endTime
                                    ? moment(item.shiftReport.endTime, "DD.MM.YYYY HH:mm").format(
                                          "HH:mm"
                                      )
                                    : "-"}
                            </span>
                            {stores.meStore.checkRole("notVisibleForCompanyUser") &&
                            item?.shiftReport?.earlyEnd > 0 ? (
                                <Tooltip
                                    title={
                                        item.shiftReport?.earlyEnd
                                            ? `${item.shiftReport?.earlyEnd}${t(
                                                  "units.minute.short",
                                                  {
                                                      ns: "common"
                                                  }
                                              )} ${t("shiftScore.earlyShiftEnd", {
                                                  ns: "shiftScore"
                                              }).toLocaleLowerCase()}`
                                            : 0
                                    }
                                >
                                    <ExclamationCircleOutlined />
                                </Tooltip>
                            ) : (
                                ""
                            )}
                        </>
                    )}
                />

                <TableContainer.Column
                    dataIndex="packageCount"
                    title={t("shiftScore.packageCount", {ns: "shiftScore"})}
                    render={(packageCount, item) => (
                        <>
                            <span data-testid="packageCount">
                                {item?.shiftReport?.packageCount || 0}
                                {" ~> "}
                                <b>{item?.revision?.packageCount || item?.packageCount}</b>
                            </span>
                        </>
                    )}
                />
                <TableContainer.Column
                    dataIndex="workTimeMinute"
                    title={` ${t("shiftScore.workTime", {
                        ns: "shiftScore"
                    })}`}
                    render={(workTimeMinute, item) => (
                        <>
                            <span data-testid="workTimeMinute">
                                {item?.shiftReport?.workTimeMinute || 0}
                                {" ~> "}
                                <b>{item?.revision?.workTimeMinute || item?.workTimeMinute}</b>
                            </span>
                        </>
                    )}
                />

                <TableContainer.Column
                    title={t("actions.action", {ns: "common"})}
                    dataIndex="_id"
                    render={renderActionButtons}
                />
            </TableContainer>
        </>
    );
};

export default ObjectionsTable;
